'use client'

import { useCallback, useEffect, useMemo } from 'react'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { type Address, parseUnits, zeroAddress } from 'viem'
import { useAccount, useConfig, useWriteContract } from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import {
  useSimulateEVCBatchCall,
  TransactionLabel,
  type BorrowTransactionItem,
  type TransactionItems,
} from '@eulerxyz/wagmi-evc'
import { useEBorrowMoreDialog } from './logic/useEBorrowMoreDialog'
import { EBorrowMoreView } from './views/EBorrowMoreView'
import { SuccessView } from '@/features/dialogs/common/views/SuccessView'
import { withSuspense } from '@/ui/utils/withSuspense'
import { DialogContentSkeleton } from '@/features/dialogs/common/components/skeletons/DialogContentSkeleton'
import { isUserRejection } from '@/lib/errors'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { invalidateBalancesCache } from '@/domain/cache'
import { useQueryClient } from '@tanstack/react-query'
import { useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import type { ActionHandlerState } from '@/features/actions/logic/types'

// =============================================================================
// Transaction Builder (wagmi-evc TransactionItems)
// =============================================================================

function buildBorrowMoreTransactions({
  liabilityVault,
  liabilityDecimals,
  liabilityAsset,
  liabilitySymbol,
  address,
  subAccountAddress,
  borrowAmountBigInt,
}: {
  liabilityVault: Address
  liabilityDecimals: number
  liabilityAsset: Address
  liabilitySymbol: string
  address: Address
  subAccountAddress: Address
  borrowAmountBigInt: bigint
}): TransactionItems {
  if (!address || address === zeroAddress || borrowAmountBigInt === 0n) return []

  const liabilityAssetInfo = {
    address: liabilityAsset,
    name: liabilitySymbol,
    symbol: liabilitySymbol,
    decimals: liabilityDecimals,
  }

  const borrowTransaction: BorrowTransactionItem = {
    label: TransactionLabel.BORROW,
    vault: liabilityVault,
    asset: liabilityAssetInfo,
    from: subAccountAddress,
    to: address,
    value: borrowAmountBigInt.toString(),
  }

  return [borrowTransaction]
}

// =============================================================================
// Container Component
// =============================================================================

interface EBorrowMoreDialogContentContainerProps {
  closeDialog: () => void
  liabilityVault: Address
  liabilitySymbol: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  currentDebtFormatted: number
  currentLTV: number
  maxLTV: number
  collateralValueUSD: number
  liabilityPriceUSD: number
  subAccountId?: number
}

function EBorrowMoreDialogContentContainerInternal({
  closeDialog,
  liabilityVault,
  liabilitySymbol,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  currentDebtFormatted,
  currentLTV,
  maxLTV,
  collateralValueUSD,
  liabilityPriceUSD,
  subAccountId,
}: EBorrowMoreDialogContentContainerProps) {
  const { address } = useAccount()
  const config = useConfig()
  const queryClient = useQueryClient()

  const {
    liabilityToken,
    additionalBorrowAmount,
    setAdditionalBorrowAmount,
    ltv,
    setLtv,
    canBorrow,
    impliedLtv,
    safeMaxLTV,
    subAccountAddress,
  } = useEBorrowMoreDialog({
    liabilityVault,
    liabilitySymbol,
    liabilityDecimals,
    liabilityAsset,
    liabilityLogoURI,
    currentDebtFormatted,
    currentLTV,
    maxLTV,
    collateralValueUSD,
    liabilityPriceUSD,
    subAccountId,
  })

  const borrowAmountBigInt = useMemo(() => {
    try {
      return parseUnits(additionalBorrowAmount.trim() || '0', liabilityDecimals)
    } catch {
      return 0n
    }
  }, [additionalBorrowAmount, liabilityDecimals])

  const transactions = useMemo(() => {
    if (!address || !subAccountAddress) return []
    return buildBorrowMoreTransactions({
      liabilityVault,
      liabilityDecimals,
      liabilityAsset,
      liabilitySymbol,
      address,
      subAccountAddress,
      borrowAmountBigInt,
    })
  }, [
    address,
    subAccountAddress,
    liabilityVault,
    liabilityDecimals,
    liabilityAsset,
    liabilitySymbol,
    borrowAmountBigInt,
  ])

  const {
    data: simulationData,
    error: simulationError,
    isLoading: isSimulating,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    query: {
      enabled: !!address && transactions.length > 0 && canBorrow,
      retry: false,
    },
  })

  const { writeContract, isPending: isWriting, error: writeError, data: txHash, reset: resetWrite } = useWriteContract()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (writeError) resetWrite()
  }, [borrowAmountBigInt])

  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })

  const handleExecute = useCallback(() => {
    if (!simulationData?.request) return
    // wagmi-evc's SimulateEVCBatchCallReturnType is a deeply generic chain-union type
    // that doesn't structurally satisfy wagmi's writeContract parameter type
    writeContract(simulationData.request as any)
  }, [simulationData, writeContract])

  const borrowTimeout = useActionTimeout({
    timeoutMs: 15000,
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError,
  })

  const handleExecuteWithTimeout = useCallback(() => {
    borrowTimeout.handleActionClick(handleExecute)
  }, [borrowTimeout, handleExecute])

  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    if (isWriting || isWaiting) return 'loading'
    if (isConfirmed) return 'success'

    const hasBlockingError =
      (simulationError && !isUserRejection(simulationError)) ||
      (writeError && !isUserRejection(writeError)) ||
      (waitError && !isUserRejection(waitError))

    if (hasBlockingError) return 'disabled'
    if (!canBorrow) return 'disabled'
    if (!simulationData) {
      return isSimulating ? 'loading' : 'disabled'
    }
    return 'ready'
  }, [
    simulationData,
    isSimulating,
    isWriting,
    isWaiting,
    isConfirmed,
    simulationError,
    writeError,
    waitError,
    canBorrow,
  ])

  const displayError = useMemo(() => {
    if (borrowTimeout.isTimedOut) return borrowTimeout.displayError
    const errors = [simulationError, writeError, waitError].filter(Boolean)
    return errors.find((err) => !isUserRejection(err as any)) || null
  }, [simulationError, writeError, waitError, borrowTimeout.isTimedOut, borrowTimeout.displayError])

  useEffect(() => {
    if (isConfirmed && address) {
      invalidateBalancesCache({
        queryClient,
        chainId: E_LEND_CHAIN_ID,
        address,
        vaultAddress: liabilityVault,
      })
    }
  }, [isConfirmed, address, queryClient, liabilityVault])

  if (isConfirmed && liabilityToken) {
    return (
      <SuccessView
        objectiveType="borrow"
        tokenWithValue={{
          token: liabilityToken,
          value: NormalizedUnitNumber(additionalBorrowAmount),
        }}
        onProceed={closeDialog}
        proceedText="Back to Position"
      />
    )
  }

  return (
    <EBorrowMoreView
      liabilityVault={liabilityVault}
      liabilityDecimals={liabilityDecimals}
      liabilitySymbol={liabilitySymbol}
      liabilityLogoURI={liabilityLogoURI}
      additionalBorrowAmount={additionalBorrowAmount}
      setAdditionalBorrowAmount={setAdditionalBorrowAmount}
      currentDebtFormatted={currentDebtFormatted}
      ltv={ltv}
      setLtv={setLtv}
      currentLTV={currentLTV}
      safeMaxLTV={safeMaxLTV}
      collateralValueUSD={collateralValueUSD}
      impliedLtv={impliedLtv}
      liabilityPriceUSD={liabilityPriceUSD}
      liabilityToken={liabilityToken}
      canBorrow={canBorrow}
      executeStatus={executeStatus}
      isSimulating={isSimulating}
      isTimedOut={borrowTimeout.isTimedOut}
      simulationError={simulationError}
      displayError={displayError}
      onExecute={handleExecuteWithTimeout}
      isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
    />
  )
}

export const EBorrowMoreDialogContentContainer = withSuspense(
  EBorrowMoreDialogContentContainerInternal,
  DialogContentSkeleton,
)
